// Client helpers for the server-backed investor/brand gates. No access secret is
// embedded here — verification and link minting both happen on the API server.

export type GateScope = "investor" | "brand";

/** Verify a gate token against the server. Returns false on any failure. */
export async function verifyGateToken(scope: GateScope, token: string): Promise<boolean> {
  if (!token) return false;
  try {
    const res = await fetch("/api/gates/verify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ scope, token }),
    });
    if (!res.ok) return false;
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean };
    return data.ok === true;
  } catch {
    return false;
  }
}

/**
 * Admin-only: mint a signed, expiring gate token via the server.
 * `authFetch` must carry the admin bearer token. Returns the token or null.
 */
export async function mintGateToken(
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  scope: GateScope,
  expiryDate: string,
): Promise<string | null> {
  try {
    const res = await authFetch(`/api/gates/${scope}-link`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expiryDate }),
    });
    if (!res.ok) return null;
    const data = (await res.json().catch(() => ({}))) as { token?: string };
    return data.token ?? null;
  } catch {
    return null;
  }
}
